<script setup lang="ts">
import { onClickOutside } from '@vueuse/core';
import { computed, nextTick, ref, watch } from 'vue';

const WHEEL_ITEM_HEIGHT = 32;

const props = withDefaults(
  defineProps<{
    disabled?: boolean;
    hourLabel?: string;
    minuteLabel?: string;
  }>(),
  {
    disabled: false,
    hourLabel: 'Часы',
    minuteLabel: 'Минуты',
  },
);

const model = defineModel<string | null>({ default: null });

const rootRef = ref<HTMLElement | null>(null);
const popoverRef = ref<HTMLElement | null>(null);
const triggerRef = ref<HTMLButtonElement | null>(null);
const hourColRef = ref<HTMLElement | null>(null);
const minuteColRef = ref<HTMLElement | null>(null);
const open = ref(false);
const popoverStyle = ref<Record<string, string>>({});

const hours = Array.from({ length: 24 }, (_, index) => String(index).padStart(2, '0'));
const minutes = Array.from({ length: 60 }, (_, index) => String(index).padStart(2, '0'));

const displayValue = computed(() => model.value ?? '—:—');
const minuteWheelDisabled = computed(() => props.disabled || !model.value);

onClickOutside(rootRef, (event) => {
  const path = event.composedPath();
  if (popoverRef.value && path.includes(popoverRef.value)) return;
  open.value = false;
});

watch(
  () => props.disabled,
  (next) => {
    if (next) open.value = false;
  },
);

function updatePopoverPosition(): void {
  const trigger = triggerRef.value;
  if (!trigger) return;
  const rect = trigger.getBoundingClientRect();
  const popoverHeight = 190;
  const gap = 6;
  const fitsBelow = rect.bottom + gap + popoverHeight <= window.innerHeight - 8;
  const top = fitsBelow ? rect.bottom + gap : rect.top - gap - popoverHeight;
  popoverStyle.value = {
    top: `${Math.max(8, top)}px`,
    left: `${rect.left + rect.width / 2}px`,
  };
}

function scrollColumn(col: HTMLElement | null, index: number): void {
  if (!col) return;
  col.scrollTop = index * WHEEL_ITEM_HEIGHT;
}

function syncScrollFromModel(): void {
  const hour = model.value?.slice(0, 2) ?? '';
  const minute = model.value?.slice(3, 5) ?? '00';
  scrollColumn(hourColRef.value, hour ? hours.indexOf(hour) + 1 : 0);
  scrollColumn(minuteColRef.value, hour ? minutes.indexOf(minute) : 0);
}

function toggleOpen(): void {
  if (props.disabled) return;
  open.value = !open.value;
  if (open.value) {
    void nextTick(() => {
      updatePopoverPosition();
      syncScrollFromModel();
    });
  }
}

function onHourScroll(): void {
  const col = hourColRef.value;
  if (!col) return;
  const index = Math.round(col.scrollTop / WHEEL_ITEM_HEIGHT);
  if (index <= 0) {
    model.value = null;
    scrollColumn(minuteColRef.value, 0);
    return;
  }
  const hour = hours[index - 1] ?? hours[0];
  const minute = model.value?.slice(3, 5) ?? '00';
  model.value = `${hour}:${minute}`;
}

function onMinuteScroll(): void {
  if (!model.value) return;
  const col = minuteColRef.value;
  if (!col) return;
  const index = Math.round(col.scrollTop / WHEEL_ITEM_HEIGHT);
  const minute = minutes[index] ?? minutes[0];
  const hour = model.value.slice(0, 2);
  model.value = `${hour}:${minute}`;
}

watch(model, () => {
  if (!open.value) return;
  void nextTick(() => syncScrollFromModel());
});
</script>

<template>
  <div
    ref="rootRef"
    class="time-of-day-input"
    :class="{ 'is-disabled': disabled, 'is-open': open }"
  >
    <button
      ref="triggerRef"
      type="button"
      class="time-of-day-input__trigger"
      :disabled="disabled"
      :aria-label="hourLabel"
      :aria-expanded="open"
      @click="toggleOpen"
    >
      {{ displayValue }}
    </button>

    <Teleport to="body">
      <div
        v-if="open"
        ref="popoverRef"
        class="time-of-day-input__popover"
        :style="popoverStyle"
        role="dialog"
        aria-modal="false"
        :aria-label="`${hourLabel}, ${minuteLabel}`"
      >
        <div class="time-of-day-input__wheel">
          <div
            ref="hourColRef"
            class="time-of-day-input__wheel-col"
            :aria-label="hourLabel"
            @scroll="onHourScroll"
          >
            <div
              class="time-of-day-input__wheel-pad"
              aria-hidden="true"
            ></div>
            <div
              class="time-of-day-input__wheel-item time-of-day-input__wheel-item--empty"
              aria-hidden="true"
            >
              —
            </div>
            <div
              v-for="hour in hours"
              :key="hour"
              class="time-of-day-input__wheel-item"
            >
              {{ hour }}
            </div>
            <div
              class="time-of-day-input__wheel-pad"
              aria-hidden="true"
            ></div>
          </div>

          <span
            class="time-of-day-input__sep"
            aria-hidden="true"
          >:</span>

          <div
            ref="minuteColRef"
            class="time-of-day-input__wheel-col"
            :class="{ 'is-disabled': minuteWheelDisabled }"
            :aria-label="minuteLabel"
            :aria-disabled="minuteWheelDisabled"
            @scroll="onMinuteScroll"
          >
            <div
              class="time-of-day-input__wheel-pad"
              aria-hidden="true"
            ></div>
            <div
              v-for="minute in minutes"
              :key="minute"
              class="time-of-day-input__wheel-item"
            >
              {{ minute }}
            </div>
            <div
              class="time-of-day-input__wheel-pad"
              aria-hidden="true"
            ></div>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>
